// central place for generic/shared functionality across Models/Rebalancing

import React from 'react';
import debug from 'debug';
import history from '../../history';
import store, {_openModal, _setProfile} from '../store';
import {_setNotification} from '../reducers/headerNotification';
import {isEntitled} from '../reducers/user';
import {_getConfig} from '../reducers/config';
import {Column, DescriptiveText, Row} from '../index';
import {formatter, parseErrorMessage} from './utils';
import axiosInstance from './axios';
import {getColorListPrimary, getColor} from './colors';
import lazy from 'lazy.js';
import Promise from 'bluebird';
import sanitizeHtml from 'sanitize-html';
import styled from 'styled-components/macro';

const log = debug('circleblack:utils:models');

const _ModelPlatformTerms = styled.div`
  text-align: left;
`;

/* Validation/Utilities */

// ETODO: improve catch behavior?
export const checkPlatformTerms = onSuccess => {
  if (isEntitled([{group: 'models', feature: 'platformCost', access: 'r'}])) {
    return Promise.join(
      axiosInstance.get('/models/platform/terms?limit=1'),
      store.dispatch(_getConfig(true)),
      terms => {
        const platformTerms = terms.data[0];
        const state = store.getState();
        const profile = state.session.profile;
        const platformCost = profile.firm.modelMarketplaceFee;

        if (profile.modelPlatformTerms !== platformTerms.version) {
          const defaultPlatformTermsText = `<ol>
            <li>You accept full fiduciary responsibility associated with the use of any models on CircleBlack.</li>
            <br/>
            <li>You accept full responsibility for any trades executed using models on CircleBlack.
              CircleBlack is not responsible for any trading issues that may arise.</li>
          </ol>`;

          store.dispatch(_openModal({
            type: 'MessageModal',
            title: 'Model Platform Terms & Conditions',
            body: <_ModelPlatformTerms dangerouslySetInnerHTML={
              {
                __html: sanitizeHtml(
                  platformCost
                    ? platformTerms.terms.replace(/{{cost}}/g, (platformCost * 100).toFixed(2))
                    : defaultPlatformTermsText
                ),
              }
            }/>,
            button: {
              text: 'Agree',
              action: () => axiosInstance.put('/user/models/platform', {modelPlatformTerms: platformTerms.version})
                .then(agree => {
                  profile.modelPlatformTerms = agree.data.modelPlatformTerms;
                  store.dispatch(_setProfile(profile));
                  if (onSuccess) {
                    onSuccess();
                  }
                })
                .catch(function handleAgreementError(err) {
                  log('error', err);
                  // ETODO PENDING REQS should this be an error modal instead? Does it matter? Expect backend error?
                  store.dispatch(_openModal({
                    type: 'MessageModal',
                    title: 'Model Platform Terms & Conditions',
                    body: 'There was a problem processing your agreement to the terms. Please try again.'
                      + ' If the issue persists please contact Client Services to resolve the issue.',
                  }));
                }),
            },
          }));
        } else if (profile.modelPlatformTerms === platformTerms.version) {
          if (onSuccess) {
            onSuccess();
          }
        }
      }
    );
  }
  if (onSuccess) {
    onSuccess();
  }
};

// ETODO 'white-space: pre-line'?
export const openCostModal = () => store.dispatch(_getConfig())
  .then(() => {
    const state = store.getState();
    const platformCost = state.session.profile.firm.modelMarketplaceFee;

    let costExplanationTextP1 = 'The model cost is the annualized fee charged by the model provider to use this '
      + 'particular model. ';

    costExplanationTextP1 += (platformCost
      ? 'This does not include the annualized ' + (platformCost * 100).toFixed(2)
        + '% CircleBlack Model Marketplace fee. '
      : '');

    costExplanationTextP1 += 'The monthly model charge is calculated by multiplying the model cost by a daily factor '
      + '(1/365) by the account balance by the total number of days the model is linked to the account during a '
      + 'particular month.';

    const body = <DescriptiveText alignment={'left'}>
      <p>{costExplanationTextP1}</p>
      <p>The total monthly model cost is the sum of the model cost and the CircleBlack Model Marketplace fee.</p>
      <p>Your fee period begins when you assign a model to an account or rebalance an account using a model from the marketplace.
        The fee period will end when you unassign the model from that account.</p>
    </DescriptiveText>;

    store.dispatch(_openModal({
      type: 'MessageModal',
      title: 'Model Cost',
      body: body,
      size: 'md',
    }));
  });

export const openAssociatedPlanModal = () => {
  store.dispatch(_openModal({
    type: 'MessageModal',
    title: 'Associated Plan',
    body: 'The Associated Plan is the group of limited investment options available for this account. '
      + 'Only models comprised of investment options that are part of the associated plan can be assigned to this account. '
      + 'This plan may be linked to additional accounts. To view the investment options, click Rebalance, Create New Model, and then Create Manually. '
      + 'Then click Add Securities to view all the investment options.',
    size: 'sm',
  }));
};

// ETODO?
// this.canAccessMarketplace = function canAccessMarketplace() {
//   var hasProvider;
//   var i;
//
//   for (i = 0; i < Session.entitlements.length; ++i) {
//     if (RegExp('modelProviders', 'g').test(Session.entitlements[i])) {
//       hasProvider = true;
//       break;
//     }
//   }
//   return Session.isEntitled(['models:marketplace:r']) && hasProvider;
// };

// ETODO?
export const viewMaterial = material => {
  switch (material.type) {
    case 'text':
    case 'file':
      store.dispatch(_openModal({
        type: 'ViewMaterial',
        material,
      }));
      break;
    case 'link':
      window.open(material.content, '_blank');
      break;
    default:
      break;
  }
};

export const buildDriftStatuses = (config, displayCondition) => {
  log('driftStatuses', config);
  if (!(config.models && config.models.driftStatuses)) {
    return [];
  }
  return Object.values(config.models.driftStatuses)
    .filter(s => s.status !== 'Not Applicable')
    .map(status => ({
      title: status.status,
      image: status.icon,
      description: status.description,
      color: status.color,
      category: 'Drift',
      displayCondition: displayCondition ? displayCondition(status) : null,
    }));
};

// ETODO Move this?
export const buildTradeStatuses = (config, displayCondition) => {
  log('tradeStatuses', config);
  if (!(config.trading && config.trading.tradeStatuses)) {
    return [];
  }
  return Object.values(config.trading.tradeStatuses)
    .filter(s => s.status !== 'FILE GENERATED') // ETODO filter out on backend?
    .sort((a, b) => a.seq - b.seq)
    .map(status => {
      const tradeStatus = {
        name: status.status,
        title: status.displayName,
        image: status.icon,
        description: status.description,
        color: status.color,
        category: 'Trading',
      };
      tradeStatus.displayCondition = displayCondition ? displayCondition(tradeStatus) : null;
      return tradeStatus;
    });
};

export const getDropdownProviders = () => axiosInstance.get('/models/providers')
  .then(res => lazy(res.data)
    .sortBy('displayName')
    .map(provider => ({
      label: provider.displayName,
      value: provider.id || 'isNull',
    }))
    .toArray())
  .catch(function error(err) {
    log('error', err);
    throw err;
  });

export const willHaveFees = models => {
  const state = store.getState();
  const platformCost = state.session.profile.firm.modelMarketplaceFee;

  return ((isEntitled([{group: 'models', feature: 'platformCost', access: 'r'}]) && platformCost)
    || models.some(model => (model.providerID && model.actualCost)));
};

export const shouldDisplayFees = models => {
  const state = store.getState();
  const platformCost = state.session.profile.firm.modelMarketplaceFee;

  return ((isEntitled([{group: 'models', feature: 'platformCost', access: 'r'}]) && platformCost)
    || models.some(model => (model.providerID && !model.isFree)));
};

export const involvesTrades = action => ~['SUBMIT_TRADES', 'GENERATE_TRADE_FILE'].indexOf(action);

// ETODO integrate into existing formatters?
export const formatCashReserve = (type, value) => {
  if (type === 'percent') {
    return (Math.round(value * 10000) / 100).toFixed(1) + '%';
  }
  return formatter('currency', {noDecimals: 1})(value);
};

// ETODO integrate into existing formatters?
export const formatCost = cost => (+cost * 100).toFixed(2) + '%';

// ETODO integrate into existing formatters?
export const formatWeighting = weighting => {
  const rounded = Math.round(weighting * 1e4) / 1e2;
  return (rounded % 1 ? rounded.toFixed(1) : rounded) + '%';
};

// ETODO overcomplicated? turn into component?
export const formatAssignedModels = (models, options) => {
  const opts = options || {};
  const array = models.map((model, idx, arr) => `${model.title} ${arr.length > 1 ? `(${formatWeighting(model.weighting)})` : ''}`);

  if (!array.length) {
    array.push(opts.emptyText ? opts.emptyText : 'None');
  }

  if (opts.cashReserve && opts.cashReserve.value) {
    array.push(`with ${formatCashReserve(opts.cashReserve.type, opts.cashReserve.value)} Cash Reserve`);
  }

  if (opts.asHTML) {
    const rowProps = {};
    if (opts.justifyHTML) {
      rowProps.justifySubRow = opts.justifyHTML;
    }
    return <Column>
      {array.map((item, idx) => <Row key={`assignedModels-${idx}`} {...rowProps}>{item}</Row>)}
    </Column>;
  }
  return array.join('\n');
};

// Broken
// export const isCashOrMMF = holding => Promise.resolve(store.dispatch(_getConfig()))
//   .then(() => {
//     const state = store.getState();
//     return holding.securityID === state.config.cashSecurityID || holding.securitySubtype === 'Money Market Fund';
//   });

export const getDoNotSell = (holding, account, updates) => {
  const changed = updates[account.id]
    && updates[account.id][holding.securityID]
    && updates[account.id][holding.securityID].hasOwnProperty('doNotSell')
    && updates[account.id][holding.securityID].doNotSell;

  return changed !== undefined ? changed : holding.doNotSell;
};

// (Currently limited to Do Not Sell)
export const saveAccountSecurities = (accountID, securities, options) => {
  const opts = options || {};

  // Do Not Sell Holdings Modal
  store.dispatch(_openModal({
    type: 'ConfirmModal',
    title: 'Do Not Sell Holdings',
    body: 'For holdings set as Do Not Sell, the model allocations will be adjusted so that these holdings are not sold when rebalancing the account.',
    button: {
      text: 'Confirm',
      action: () => axiosInstance.put(
        `/accounts/securities${opts.driftMethod ? '?driftMethod=' + opts.driftMethod : ''}`,
        [{id: accountID, securities}]
      )
        .then(() => {
          if (opts.onSuccess) {
            opts.onSuccess();
          }
          store.dispatch(_setNotification({message: 'Do Not Sell settings were successfully updated.'}));
        })
        .catch(err => {
          log('error', err);
          store.dispatch(_setNotification({message: parseErrorMessage(err)}));
        }),
    },
  }));
};

// ETODO use across all of models?
export const buildOptions = (account, theme) => {
  const options = [];
  const colorList = [...getColorListPrimary(theme)];

  // Get proposed
  if (account.proposed) {
    account.proposed.forEach(proposed => {
      options.push({
        securityID: proposed.securityID,
        securityName: proposed.securityName,
        securityType: proposed.securityType,
        securitySubtype: proposed.securitySubtype,
        ticker: proposed.ticker,
        current: 0,
        proposed: proposed.value / 100,
        color: getColor(colorList),
      });
    });
  }

  // Get current
  if (account.current) {
    account.current.forEach(current => {
      const existing = options.find(opt => opt.securityID === current.securityID);
      if (existing) {
        existing.current = (current.value || 0) / 100;
        existing.doNotSell = current.doNotSell;
        existing.included = current.included;
      } else if (current.value !== 0) {
        const option = {
          securityID: current.securityID,
          securityName: current.securityName,
          securityType: current.securityType,
          securitySubtype: current.securitySubtype,
          ticker: current.ticker,
          current: current.value / 100,
          proposed: 0,
          color: getColor(colorList),
          doNotSell: current.doNotSell,
          included: current.included,
        };
        if (!current.securityID) {
          option.securityName = 'Data Issues';
          option.color = '#FF0000';
        } else {
          option.color = getColor(colorList);
        }
        option.current = current.value / 100;
        option.proposed = 0;
        options.push(option);
      }
    });
  }
  // Determine if option is drifted
  if (account.balancingAlert === 'Drifted') {
    options.forEach(option => {
      if (Math.abs(option.current - option.proposed) > (account.driftThreshold)) {
        option.drifted = true;
      }
    });
  }
  return options.filter(o => o.current || o.proposed);
};

// ETODO ugly: solution involving backend is ideal. Also see orm.models.getAgreements, it should be simplified
export const buildTerms = model => {
  const terms = {};

  if (model.terms) {
    terms.level = 'model';
    terms.content = model.terms;
    terms.version = model.termsVersion;
  } else if (model.provider && model.provider.terms) {
    terms.level = 'provider';
    terms.content = model.provider.terms;
    terms.version = model.provider.termsVersion;
  }
  return terms;
};

export const grantModelAccess = (model, onSuccess) => {
  if (model.provider && model.provider.authType) {
    // ETODO
    // return openModal({
    //   type: 'ConfirmModal',
    //   title: 'Continue to Provider',
    //   body: `You need to continue to ${model.provider.displayName}\'s website to complete the process. Once we get confirmation of approval from them you will see the model in your My Models list and can apply it to accounts for rebalancing.`,
    //   button: {
    //     text: `Go to ${model.provider.displayName}`,
    //     action: () => {},
    //   }
    // })
  }
  const session = store.getState().session;
  return axiosInstance.post(`/models/${model.id}/advisors/${session.profile.id}`)
    .then(() => {
      if (onSuccess) {
        onSuccess();
      }
      store.dispatch(_setNotification({message: `${model.title} has been successfully added to your My Models list and can now be applied to accounts for rebalancing.`}));
    })
    .catch(() => {
      // ETODO error copy?
      store.dispatch(_openModal({
        type: 'MessageModal',
        title: 'Add Model',
        body: 'There was a problem with your access to the model. Please contact Client Services to resolve the issue.',
      }));
    });
};

/* Model Flow Control */

// ETODO maybe, because 55ip?
// export const startModelCreation = () => {
//   // self.clearCurrentModels();
//   // self.setModelAction('create');
//   if (isEntitled([{group: 'modelProviders', feature: '55ip', access: 'r'}])) {
//     // NewModelTypeModal.open();
//   } else {
//     // NewModelModal.open();
//   }
// };

// ETODO Change to conditionally take model ID?
export const startRemoveModel = (model, onSuccess) => {
  const userID = store.getState().session.profile.id;
  const showDelete = !(model.providerID || model.createdBy !== userID);
  const config = {
    title: 'Remove Model',
  };

  if (showDelete) {
    config.message = 'This action will permanently delete the '
      + model.title
      + ' model. All accounts that this model was assigned to will have their models unassigned.';
    config.confirm = {
      action: () => axiosInstance.delete(`/models/${model.id}`),
      buttonText: 'Delete Model',
      successMessage: 'Model ' + model.title + ' has been deleted.',
    };
  } else {
    config.message = 'This action will remove the '
      + model.title
      + ' model from your My Models list. All accounts that this model was assigned to will have their models unassigned.';
    config.confirm = {
      action: () => axiosInstance.delete(`models/${model.id}/advisors/${userID}`),
      buttonText: 'Remove Model',
      successMessage: 'Model ' + model.title + ' has been removed.',
    };
  }

  store.dispatch(_openModal({
    type: 'ConfirmModal',
    title: config.title,
    body: config.message,
    button: {
      text: config.confirm.buttonText,
      action:
        () => config.confirm.action()
          .then(function confirmSuccess() {
            if (onSuccess) {
              onSuccess();
            }
            store.dispatch(_setNotification({message: config.confirm.successMessage}));
          })
          .catch(err => {
            log('error', err);
            store.dispatch(_openModal({type: 'Error', title: config.title, err: err}));
          }),
    },
  }));
};

// ETODO Change to conditionally take account object?
export const startUnassignModels = (accountID, onSuccess) => {
  axiosInstance.get(`/accounts/${accountID}/rebalancing`)
    .then(res => {
      const account = res.data;
      const hasMultiple = account.models.length > 1;
      const title = `Unassign Model${hasMultiple ? 's' : ''}`;
      const message = hasMultiple
        ? `This will unassign all of the models from the account ${account.name} and will stop tracking drift for the account. It will not rebalance the account in any way.`
        : `This will unassign the model ${account.models[0].title} from the account ${account.name} and will stop tracking drift for the account. It will not rebalance the account in any way.`;

      store.dispatch(_openModal({
        type: 'ConfirmModal',
        title: title,
        body: message,
        button: {
          action: () => axiosInstance.delete(`/accounts/${account.id}/models`)
            .then(() => {
              if (onSuccess) {
                onSuccess();
              }
              store.dispatch(_setNotification({
                message: `The ${hasMultiple ? 'models were' : 'model was'} successfully unassigned from the account.`,
              }));
            })
            .catch(err => {
              log('error', err);
              // ETODO error copy?
              store.dispatch(_openModal({
                type: 'MessageModal',
                title: title,
                body: `There was a problem unassigning the ${hasMultiple
                  ? 'models'
                  : 'model'} from the account. Please contact Client Services to resolve the issue.`,
              }));
            }),
        },
      }));
    })
    .catch(err => {
      log('error', err);
      // ETODO Jeremy? would prefer not to need to know how many models here
      store.dispatch(_openModal({type: 'Error', title: 'Error', err: err}));
    });
};

/* Rebalancing Flow Control - Shared */

export const assignAccountsToModels = (accounts, models) => axiosInstance.put('/accounts/models', {
  accounts: accounts.map(account => account.id),
  models: models.length > 1
    ? models.filter(model => !!model.weighting).map(model => ({id: model.id, weighting: model.weighting}))
    : models.map(model => ({id: model.id, weighting: 1})),
})
  .catch(err => {
    log('error', err);
    throw err;
  });

export const startRebalanceAccounts = (accountIDs, context) => {
  history.push(`/models/accounts/rebalance?accounts=${accountIDs}&context=${context}`);
};

/* Rebalancing Flow Control - Accounts Context */

// ETODO maybe make work for >1 account eventually (POST /accounts/rebalancing/validation, pass account IDs and boolean models param)
export const startManageAccountModels = accountID => checkPlatformTerms(
  () => axiosInstance.get(`/accounts/${accountID}/rebalancing`)
    .then(res => {
      const account = res.data;
      const manageAccountModels = {pathname: `/models/accounts/${account.id}`, state: {account}};

      if (account.models && account.models.length) {
        if (account.models.some(model => model.noAccess)) {
          store.dispatch(_openModal({
            type: 'ConfirmModal',
            title: 'No Access to Model',
            body: 'This account is shared with another advisor. You do not have access to '
              + (account.modelsCount > 1 ? 'a' : 'the')
              + ' model currently assigned to this account.'
              + 'In order to rebalance this account, please select or create a different model.',
            button: {
              text: 'Continue',
              action: history.push(manageAccountModels),
            },
          }));
        } else if (account.models.some(model => model.isInvalidForPlan)) {
          store.dispatch(_openModal({
            type: 'ConfirmModal',
            title: 'Need Different Model',
            body: 'The current '
              + (account.models.length === 1 ? 'model contains' : 'models contain')
              + ' securities that are not valid with this account’s plan.',
            button: {
              text: 'OK',
              action: history.push(manageAccountModels),
            },
          }));
        } else {
          startRebalanceAccounts([account.id], 'accounts');
        }
      } else {
        history.push(manageAccountModels);
      }
    })
    .catch(() => store.dispatch(_openModal({
      type: 'MessageModal',
      title: 'Error Rebalancing',
      err: 'We encountered an issue preventing you from rebalancing at this time. Please try again later. We apologize for the inconvenience',
    })))
);

export const openRebalancingIssuesModal = acct => {
  const {rebalancingIssues} = acct;
  let issueText;

  if (rebalancingIssues.includes('NO_POS') || rebalancingIssues.includes('UNMAPPED_POS')) {
    issueText = 'This account cannot be rebalanced at this time as there is an issue with the data.\n'
      + 'Our data team has been notified and you will receive confirmation once the issue has been fixed.';
  } else if (rebalancingIssues.includes('NO_PLAN')) {
    issueText = 'CircleBlack is working on pulling in your plan\'s investment choices. We will notify you when they are available.';
  } else if (rebalancingIssues.includes('PARTIAL_PLAN')) {
    issueText = 'Your account\'s plan is currently under review. When it is ready, we will notify you.';
  }

  axiosInstance.post('/accounts/email/rebalancing', {accounts: [acct.id]});
  store.dispatch(_openModal({
    type: 'MessageModal',
    title: 'Cannot Rebalance',
    body: issueText,
  }));
};

/* Rebalancing Flow Control - Models Context */

// ETODO accept models (to avoid repeat API)
export const startManageModelAccounts = modelIDs => {
  history.push(`/models/accounts?models=${modelIDs}`);
};

/* Rebalance Submission */

export const generateProposalAction = {
  name: 'GENERATE_PROPOSAL',
  displayName: 'Generate Proposal',
  partnerName: null,
  partnerDisplayName: null,
  level: null,
  descriptionPastTense: 'Proposal Generated',
};

// ETODO move?
export const createTradeFile = file => {
  let csvData;
  let csvURL;
  let tempLink;

  csvData = new Blob([file.csv], {type: 'text/csv;charset=utf-8'});
  if (navigator.msSaveBlob) {
    csvURL = navigator.msSaveBlob(csvData, file.fileName);
  } else {
    csvURL = window.URL.createObjectURL(csvData);
  }
  tempLink = document.createElement('a');
  tempLink.href = csvURL;
  tempLink.setAttribute('download', file.fileName);
  tempLink.click();
};
